import { useEffect, useState } from 'react'
import { useAppSelector } from 'utils/custom-hooks/useTypedReduxStore'
import { useGetCurrencyConversionsQuery } from 'services/currencyConversions'
import Big from 'big.js'
import { GS_ROLE } from 'utils/constants'

interface PaymentAuthResponse {
  isAuthorized: boolean
  hasThreshold: boolean
  ticketValueThreshold?: string
  convertedThreshold?: string
}

const usePaymentAuthorization = (paymentAmount: number, paymentCurrency?: string): PaymentAuthResponse => {
  const [isAuthorized, setIsAuthorized] = useState(true)
  const [convertedThreshold, setConvertedThreshold] = useState<number | undefined>(undefined)

  const permissions = useAppSelector(state => state.permissions)
  const isYardAdmin = !!permissions.roles?.find(r => r.roleName === GS_ROLE.YARD_ADMIN)

  const { settings } = useAppSelector(state => state.settings)

  const highBalancePaymentAuthorization = settings.purchaseSettings.payment?.highBalancePaymentAuthorization
  const { hasThreshold, ticketValueThreshold } = highBalancePaymentAuthorization || {}
  const [userThreshold, setUserThreshold] = useState<string | null>()

  // get currency conversions
  const { data: currencyConversionsData } = useGetCurrencyConversionsQuery({})

  // Sets the user threshold based on role
  useEffect(() => {
    if (!paymentAmount) return

    // role based threshold logic
    if (highBalancePaymentAuthorization?.roleBasedThresholds) {
      const { roleBasedThresholds } = highBalancePaymentAuthorization
      const userRole = permissions.roles[0]?.roleName
      const roleThreshold = roleBasedThresholds.find(role => role.id === userRole)

      if (!roleThreshold?.enabled) return
      else {
        setUserThreshold(roleThreshold.threshold)
      }
    }
  }, [paymentAmount, permissions, highBalancePaymentAuthorization])

  // Handles currency conversion and authorization logic
  useEffect(() => {
    if (!paymentAmount || !userThreshold) return

    let thresholdToCheck = Number(userThreshold)
    const fxRate = currencyConversionsData?.exchanges.find(exchangeData => exchangeData.fc === paymentCurrency)
    const homeCurrency = currencyConversionsData?.homeCurrency

    if (paymentCurrency && paymentCurrency !== homeCurrency && fxRate) {
      thresholdToCheck = Big(userThreshold).times(Big(fxRate.rate)).round(2).toNumber()
      setConvertedThreshold(thresholdToCheck)
    }

    if (isYardAdmin) {
      setIsAuthorized(true)
    } else if (hasThreshold || highBalancePaymentAuthorization?.roleBasedThresholds) {
      setIsAuthorized(paymentAmount < thresholdToCheck)
    }
  }, [
    paymentAmount,
    permissions,
    hasThreshold,
    currencyConversionsData,
    paymentCurrency,
    userThreshold,
    highBalancePaymentAuthorization,
  ])

  return {
    isAuthorized,
    hasThreshold: hasThreshold || false,
    ticketValueThreshold: userThreshold || undefined,
    convertedThreshold: convertedThreshold?.toString(),
  }
}

export default usePaymentAuthorization
